import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  CloseCircleOutlined,
  CompassOutlined,
  DeleteOutlined,
  DollarOutlined,
  EditOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  ShareAltOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import { PageHeaderWrapper, PageLoading } from "@ant-design/pro-layout";
import { Button, Col, Layout, message, Modal, Row, Tag, Result } from "antd";
import { connect } from "dva";
import { router } from "umi";
import {
  FlagNotesType,
  HardRouteLocation,
  LoadPaymentStatusToMelioStatusMap,
  NoteType,
  QuoteStatus,
  ShipmentStatus,
} from "@/utils/enum";
import { DEFAULT_ID, getUrlParams } from "@/utils/utils";
import { getSingleAdminShipment } from "@/services/shipments";
import CancelShipmentModal from "@/components/CancelShipmentModal/CancelShipmentModal";
import CopyableGUID from "@/components/CopyableGUID";
import DealerExchangeAlert from "@/components/DealerExchangeAlert";
import DealerExchangeModal from "@/components/DealerExchangeModal";
import DispatchStepsModal from "@/components/DispatchStepsModal";
import MoreMenu from "@/components/MoreMenu";
import Notes, { NotesListPaginationType } from "@/components/Notes";
import SecurePassTag from "@/components/SecurePass/SecurePassTag/SecurePassTag";
import ShareMyRide from "@/components/ShareMyRide";
import DeliveryTimes from "./components/DeliveryTimes";
import LotExitAlert from "./components/LotExitAlert";
import ShipmentStatusTracker from "./components/ShipmentStatusTracker";
import ShipperTransporterIntegrationsCard from "./components/ShipperTransporterIntegrationsCard";
import TotalPriceCard from "./components/TotalPriceCard";
import TripDetails from "./components/TripDetails";
import VehiclesList from "./components/VehiclesList";
import ChangeFlagModal from "./ChangeFlagModal";
import HardRouteModal from "./HardRouteModal";
import ToggleHoldModal from "./ToggleHoldModal";
import UpdateLocationModal from "./UpdateLocationModal";
import styles from "./style.less";

const { Content } = Layout;
const { confirm, success } = Modal;

const AdminShipmentView = ({
  dispatch,
  adminShipmentView,
  loading,
  loadingLocation,
  configPerm,
  loadingOpsAssisted,
  securePassEnabled,
}) => {
  const [visibleUpdateLocationModal, setVisibleUpdateLocationModal] = useState(false);
  const [onHoldVisible, setOnHoldVisible] = useState(false);
  const [isOpsAssistedChecked, setIsOpsAssistedChecked] = useState(false);
  const [exchangeVehiclesAllQuoted, setExchangeVehiclesAllQuoted] = useState(false);
  const [exchangeShipment, setExchangeShipment] = useState(null);
  const [problemRide, setProblemRide] = useState(false);
  const [directBrokerage, setDirectBrokerage] = useState(false);
  const [shipmentExpedited, setShipmentExpedited] = useState(false);
  const [dealerExchangeRequired, setDealerExchangeRequired] = useState(false);
  const [visible, setVisible] = useState(false);
  const [isDamaged, setIsDamaged] = useState(false);
  const [askingForFlagNotes, setAskingForFlagNotes] = useState(null);
  const [hardRouteLocation, setHardRouteLocation] = useState(null);
  const [hardRoutePopupVisible, setHardRoutePopupVisible] = useState(false);
  const [dispatchStepsModalVisible, setDispatchStepsModalVisible] = useState(false);
  const [cancelModalVisible, setCancelModalVisible] = useState(false);
  const [shareModalVisible, setShareModalVisible] = useState(false);
  const adminShipmentData = adminShipmentView.data;
  const shipmentId = getUrlParams("shipmentId");

  const notesRef = useRef(null);

  const initializePageData = useCallback(() => {
    window.scrollTo(0, 0);

    dispatch({
      type: "adminShipmentView/fetchSingleAdminShipment",
      shipmentId,
    }).then(response => {
      if (response === undefined) {
        return;
      }

      const { shipmentData } = response;

      if (!shipmentData) {
        return;
      }

      const exchangeId = shipmentData.childShipmentId || shipmentData.parentShipmentId;

      if (exchangeId) {
        // fetch data of exchange shipment
        getSingleAdminShipment(exchangeId)
          .then(exchangeShipmentData => {
            const vehiclesAllQuoted = exchangeShipmentData?.vehicles?.every(
              v => v.quote.quoteId !== DEFAULT_ID && v.quote.status === QuoteStatus.Completed && !v.quote.expired,
            );
            setExchangeShipment(exchangeShipmentData);
            setExchangeVehiclesAllQuoted(vehiclesAllQuoted);
          })
          .catch(err => {
            console.error(err);
            message.error("Error checking exchange shipment");
          });
      }

      setShipmentExpedited(shipmentData.isExpedited);
      setVisible(shipmentData.isVisible);
      setProblemRide(shipmentData.isProblemRide);
      setDirectBrokerage(shipmentData.isDirectBrokerage);
      setDealerExchangeRequired(shipmentData.isExchangeRequired);
      setIsDamaged(shipmentData?.load?.isDamaged);
      setHardRouteLocation(shipmentData.hardRouteLocation);
      setIsOpsAssistedChecked(shipmentData?.load?.isOpsAssisted);
    });
  }, [dispatch, shipmentId]);

  useEffect(() => {
    initializePageData();
  }, [initializePageData]);

  useEffect(() => {
    const shipmentIdURLParam = getUrlParams("shipmentId");

    // Id was changed, pull new data without page refresh
    if (
      adminShipmentData &&
      adminShipmentData.id &&
      shipmentIdURLParam &&
      adminShipmentData.id !== shipmentIdURLParam &&
      !loading &&
      !loadingLocation
    ) {
      initializePageData();
    }
  }, [adminShipmentData, loading, loadingLocation, initializePageData]);

  const onMoreMenuItemClick = key => {
    const loadId = adminShipmentData?.load?.id;
    const { loadPaymentStatus } = adminShipmentData;
    const isDealerExchange = adminShipmentData?.childShipmentId || adminShipmentData?.parentShipmentId;

    switch (key) {
      case "force-location":
        setVisibleUpdateLocationModal(true);
        break;

      case "toggle-hold":
        setOnHoldVisible(true);
        break;

      case "dealer-invoice":
        window.open("", "_blank"); // invoice url???
        break;

      case "transporter-bill": {
        const loadIdFirst8 = loadId?.split("-")[0]?.toUpperCase();
        if (!loadIdFirst8) return;

        const melioBaseUrl = new URL(REACT_APP_TRANSPORTER_BILL_URL);
        const params = new URLSearchParams();

        params.set("search", loadIdFirst8);
        params.set("status", LoadPaymentStatusToMelioStatusMap[loadPaymentStatus]);

        window.open(`${melioBaseUrl}/?${params}`);
        break;
      }

      case "share": {
        /**
         * ShareMyRide has a Modal child and it's inside a Menu.Item
         * component, this cause closing the Modal to trigger this callback
         * again, so we have one trigger when the user want's to open the
         * Modal and another when they want to close it.
         */
        if (!shareModalVisible) {
          setShareModalVisible(true);
        }

        break;
      }

      case "cancel":
        if (!isDealerExchange) {
          setCancelModalVisible(true);
          break;
        }

        confirm({
          title: "Warning",
          content:
            "This shipment is part of a dealer exchange. If you cancel this shipment or set its status to Incomplete, the other shipment in the exchange may also be canceled or set to Incomplete status if the exchange is ordered type. Are you sure?",
          onOk() {
            setCancelModalVisible(true);
          },
        });

        break;

      default:
        break;
    }
  };

  const extraMenu = (currentShipmentId, onHold, canShare, canHold, canForceLocation, canInvoice, canCancel) => (
    <div key="more-menu-container" style={{ marginRight: "6px" }}>
      <MoreMenu
        key="more-menu-action"
        onItemClick={onMoreMenuItemClick}
        items={[
          {
            key: "force-location",
            icon: <CompassOutlined />,
            label: "Force Location",
            authority: canForceLocation,
          },
          {
            key: "share",
            icon: <ShareAltOutlined />,
            authority: canShare,
            label: (
              <ShareMyRide
                isMenuItem
                shipmentId={currentShipmentId}
                modalVisible={shareModalVisible}
                setModalVisible={isVisible => setShareModalVisible(isVisible)}
              />
            ),
          },
          {
            key: "toggle-hold",
            icon: onHold ? <PlayCircleOutlined /> : <PauseCircleOutlined />,
            label: onHold ? "Unhold" : "Hold",
            authority: canHold,
          },
          {
            key: "dealer-invoice",
            icon: <DollarOutlined />,
            label: "Dealer Invoice",
            disabled: true, //! dealerInvoiceUrl,
            authority: canInvoice,
          },
          {
            key: "transporter-bill",
            icon: <DollarOutlined />,
            label: "Transporter Bill",
            authority: canInvoice,
          },
          {
            key: "cancel",
            icon: <CloseCircleOutlined />,
            label: "Cancel",
            authority: canCancel,
          },
        ]}
      />
    </div>
  );

  const handleClickCreateDealerExchange = () => {
    dispatch({
      type: "adminShipmentView/toggleDealerExchangeModalVisible",
      payload: true,
    });
  };

  const handleClickUnlinkDealerExchange = () => {
    confirm({
      title: "Warning",
      content:
        "This will remove the connection between both shipments in the exchange. Both shipments will remain in the system. Are you sure?",
      onOk() {
        const hide = message.loading("Unlinking shipments...");
        dispatch({
          type: "adminShipmentView/unlinkDealerExchange",
          shipmentId,
        })
          .then(res => {
            if (res.error) {
              message.error("Could not unlink shipments");
            } else {
              message.success("Successfully unlinked shipments");
            }
          })
          .catch(err => {
            console.error(err);
            message.error("Could not unlink shipments");
          })
          .finally(() => {
            hide();
          });
      },
    });
  };

  const deleteShipment = confirmedDeleteExchange => {
    const shipmentData = adminShipmentView.data;

    if ((shipmentData.childShipmentId || shipmentData.parentShipmentId) && !confirmedDeleteExchange) {
      confirm({
        title: "Warning",
        content:
          "This shipment is part of a dealer exchange. If this shipment is deleted, the other shipment in the exchange will also be deleted. Are you sure?",
        onOk() {
          deleteShipment(true);
        },
      });
      return;
    }

    confirm({
      title: "Delete Shipment?",
      content: "Are you sure you want to delete this shipment? This action cannot be undone.",
      onOk() {
        const hide = message.loading("Deleting");
        dispatch({
          type: "adminShipmentView/removeShipment",
          payload: { shipmentId },
        }).then(() => {
          success({ title: "Successfully deleted shipment" });
          hide();
          router.push(`/admin/shipments`);
        });
      },
    });
  };

  const extraActionButtons = () => {
    const shipmentData = adminShipmentView.data;
    const { childShipmentId, parentShipmentId, status } = shipmentData;
    const shipmentIsPostedOrEarlier = status <= ShipmentStatus.Posted;
    const shipmentIsNotYetPosted = status < ShipmentStatus.Posted;
    const shipmentIsPostedOrLater = status >= ShipmentStatus.Posted;
    const shipmentAlreadyInDealerExchange = childShipmentId || parentShipmentId;
    const canEditShipment =
      configPerm["CoreService.AdminShipments.Edit"] && shipmentData?.status !== ShipmentStatus.Incomplete;
    const canDeleteShipment =
      configPerm["CoreService.AdminShipments.Delete"] &&
      [ShipmentStatus.Draft, ShipmentStatus.Pending, ShipmentStatus.Completed].includes(shipmentData?.status);

    const parentShipmentIsAssignedOrLater = exchangeShipment?.status >= ShipmentStatus.Assigned && parentShipmentId;

    const canCreateDealerExchange =
      configPerm["CoreService.AdminShipments.Create"] && shipmentIsNotYetPosted && !shipmentAlreadyInDealerExchange;

    const canUnlinkDealerExchange =
      shipmentIsPostedOrEarlier &&
      shipmentAlreadyInDealerExchange &&
      configPerm["CoreService.AdminShipments.Edit"] &&
      !parentShipmentIsAssignedOrLater;

    const canShare =
      configPerm["CoreService.AdminShipments.Share"] &&
      [ShipmentStatus.Posted, ShipmentStatus.Assigned].includes(shipmentData.status);

    const canHold =
      configPerm["CoreService.AdminShipments.ToggleOnHold"] &&
      [ShipmentStatus.Posted, ShipmentStatus.Assigned].includes(shipmentData.status);

    const canForceLocation =
      configPerm["CoreService.AdminShipments.ForceLocation"] &&
      [ShipmentStatus.Assigned, ShipmentStatus.Delivered].includes(shipmentData.status);

    const canInvoice =
      configPerm["CoreService.AdminShipments.Invoice"] && shipmentData.status === ShipmentStatus.Delivered;

    const canCancel =
      [ShipmentStatus.Posted, ShipmentStatus.Assigned, ShipmentStatus.Delivered].includes(shipmentData?.status) &&
      !!configPerm["CoreService.AdminShipments.Cancel"];

    const canViewMoreMenu = (canShare || canHold || canForceLocation || canCancel) && shipmentIsPostedOrLater;

    const actionButtons = [];

    if (canEditShipment) {
      actionButtons.push(
        <Button
          key="edit-shipment"
          icon={<EditOutlined />}
          onClick={() => router.push(`/admin/shipments/edit?shipmentId=${shipmentData.id}&admin=true`)}
        >
          Edit
        </Button>,
      );
    }

    if (canDeleteShipment) {
      actionButtons.push(
        <Button key="delete-shipment" type="danger" onClick={() => deleteShipment()} icon={<DeleteOutlined />}>
          Delete
        </Button>,
      );
    }

    if (canCreateDealerExchange) {
      actionButtons.push(
        <Button key="create-dealer-exchange" onClick={handleClickCreateDealerExchange} icon={<SwapOutlined />}>
          Create Dealer Trade
        </Button>,
      );
    }

    if (canUnlinkDealerExchange) {
      actionButtons.push(
        <Button key="unlink-dealer-exchange" onClick={handleClickUnlinkDealerExchange} icon={<SwapOutlined />}>
          Unlink Dealer Trade
        </Button>,
      );
    }

    if (canViewMoreMenu) {
      actionButtons.push(
        extraMenu(shipmentData.id, shipmentData.onHold, canShare, canHold, canForceLocation, canInvoice, canCancel),
      );
    }

    return actionButtons;
  };

  const cancelFlagToggle = () => {
    setAskingForFlagNotes(null);
    setShipmentExpedited(
      askingForFlagNotes === FlagNotesType.AutosledExpedited ? !shipmentExpedited : shipmentExpedited,
    );
    setProblemRide(askingForFlagNotes === FlagNotesType.ProblemRide ? !problemRide : problemRide);
    setIsDamaged(askingForFlagNotes === FlagNotesType.IsDamaged ? !isDamaged : isDamaged);
  };

  const handleClickHardRoutePickup = () => {
    setHardRoutePopupVisible(true);
    setHardRouteLocation(HardRouteLocation.DifficultPickup);
  };

  const handleClickHardRouteDropoff = () => {
    setHardRoutePopupVisible(true);
    setHardRouteLocation(HardRouteLocation.DifficultDropoff);
  };

  const handleToggleAutosledExpeditedChange = e => {
    const newValue = e.target.checked;
    setShipmentExpedited(newValue);
    setAskingForFlagNotes(FlagNotesType.AutosledExpedited);
  };

  const handleToggleDealerExchangeRequiredChange = e => {
    const hide = message.loading("Updating Exchange Required status.", 0);

    dispatch({
      type: "adminShipmentView/toggleDealerExchangeRequired",
      shipmentId: adminShipmentData.id,
    }).then(() => {
      hide();
      setDealerExchangeRequired(e.target.checked);
      setAskingForFlagNotes(null);
    });
  };

  const handleToggleProblemRideChange = e => {
    const newValue = e.target.checked;

    setProblemRide(newValue);
    setAskingForFlagNotes(FlagNotesType.ProblemRide);
  };

  const handleToggleVisibleChange = e => {
    const hide = message.loading("Updating Visibility status.", 0);
    dispatch({
      type: "adminShipmentView/toggleVisible",
      shipmentId: adminShipmentData.id,
    }).then(() => {
      hide();
      setVisible(e.target.checked);
      setAskingForFlagNotes(null);
    });
  };

  const handleToggleDirectBrokerageChange = e => {
    const hide = message.loading("Updating Direct Brokerage status.", 0);

    dispatch({
      type: "adminShipmentView/toggleDirectBrokerage",
      shipmentId: adminShipmentData.id,
    }).then(() => {
      hide();
      setDirectBrokerage(e.target.checked);
      setAskingForFlagNotes(null);
    });
  };

  const handleToggleOpsAssisted = e => {
    const currentOpsAssistedValue = e?.target?.checked;
    // avoid multiple toggles while request is pending
    if (loadingOpsAssisted) return;

    const loadId = adminShipmentData?.load?.id;

    setIsOpsAssistedChecked(currentOpsAssistedValue);
    const hide = message.loading("Updating Ops Assisted status.", 0);

    dispatch({
      type: "adminShipmentView/toggleOpsAssisted",
      shipmentId,
      loadId,
    }).then(res => {
      hide();

      if (res?.error) {
        message.error("Could not update Ops Assisted status.");
        setIsOpsAssistedChecked(!currentOpsAssistedValue);
        return;
      }

      message.success("Successfully updated Ops Assisted status.");
    });
  };

  const handleToggleDamaged = e => {
    const newValue = e.target.checked;

    setIsDamaged(newValue);
    setAskingForFlagNotes(FlagNotesType.IsDamaged);
  };

  const toggleDispatchStepsModalVisible = () => {
    setDispatchStepsModalVisible(!dispatchStepsModalVisible);
  };

  const onDealerExchangeSuccess = isRequired => {
    setDealerExchangeRequired(isRequired);
  };

  if (
    (typeof loading === "undefined" || loading === true) &&
    (typeof loadingLocation === "undefined" || loadingLocation === true)
  ) {
    return <PageLoading />;
  }

  if (!adminShipmentData || !shipmentId) {
    return (
      <Result
        status="404"
        title="Shipment Not Found"
        subTitle="The shipment you are looking for does not exist or you may not have permission to view it."
        extra={
          <Button type="primary" onClick={() => router.push("/admin/shipments")}>
            Back to Shipments
          </Button>
        }
      />
    );
  }

  return (
    <PageHeaderWrapper
      onBack={() => window.history.back()}
      title={
        <span>
          Shipment Id:
          <CopyableGUID guid={adminShipmentData?.id} itemStyle={{ marginLeft: 5 }} />
        </span>
      }
      subTitle={
        <>
          <span style={{ color: "#000", fontSize: "16px", fontWeight: "500" }}>
            {`Autosled Manager: ${adminShipmentData?.autosledManagerName ?? "-"}`}
          </span>
          {securePassEnabled && (
            <span style={{ marginLeft: 5 }}>
              <SecurePassTag tagStatus={adminShipmentData?.securePass} />
            </span>
          )}
          {adminShipmentData.onHold && (
            <Tag color="red" style={{ marginLeft: 8 }}>
              Hold
            </Tag>
          )}
        </>
      }
      extra={<section style={{ display: "flex", gap: "8px" }}>{extraActionButtons()}</section>}
    >
      <Layout>
        <Content className={styles.contentWrapper}>
          <Row>
            <Col xl={16} lg={24}>
              <ShipmentStatusTracker
                isDamaged={isDamaged}
                handleToggleDamaged={handleToggleDamaged}
                shipmentExpedited={shipmentExpedited}
                visible={visible}
                problemRide={problemRide}
                directBrokerage={directBrokerage}
                dealerExchangeRequired={dealerExchangeRequired}
                exchangeShipment={exchangeShipment}
                handleToggleAutosledExpedited={handleToggleAutosledExpeditedChange}
                handleToggleVisible={handleToggleVisibleChange}
                handleToggleProblemRideChange={handleToggleProblemRideChange}
                handleToggleDirectBrokerage={handleToggleDirectBrokerageChange}
                handleToggleDealerExchangeRequired={handleToggleDealerExchangeRequiredChange}
                refreshData={() => initializePageData()}
              />
              <Row style={{ marginBottom: 16 }}>
                {adminShipmentData.childShipmentId && (
                  <Row style={{ width: "97%" }}>
                    <Col flex={1}>
                      <DealerExchangeAlert
                        relativeUrl="/admin/shipments/view"
                        shipmentId={adminShipmentData.childShipmentId}
                        exchangeType={adminShipmentData.dealerExchangeType}
                        isRequired={adminShipmentData.isExchangeRequired}
                        style={{
                          width: "100%",
                          marginLeft: 28,
                          marginBottom: 16,
                        }}
                      />
                    </Col>
                  </Row>
                )}

                {adminShipmentData.parentShipmentId && (
                  <Row style={{ width: "97%" }}>
                    <Col flex={1}>
                      <DealerExchangeAlert
                        isChild
                        relativeUrl="/admin/shipments/view"
                        shipmentId={adminShipmentData.parentShipmentId}
                        exchangeType={adminShipmentData.dealerExchangeType}
                        isRequired={adminShipmentData.isExchangeRequired}
                        style={{
                          width: "100%",
                          marginLeft: 20,
                          marginBottom: 16,
                        }}
                      />
                    </Col>
                  </Row>
                )}

                <LotExitAlert />

                <TripDetails
                  handleClickHardRoutePickup={handleClickHardRoutePickup}
                  handleClickHardRouteDropoff={handleClickHardRouteDropoff}
                />
              </Row>
              <Row>
                <Col xs={24}>
                  <DeliveryTimes exchangeShipmentStatus={exchangeShipment?.status} />
                </Col>
              </Row>
              <Row>
                <Col xs={24}>
                  <VehiclesList
                    exchangeShipmentStatus={exchangeShipment?.status}
                    exchangeVehiclesAllQuoted={exchangeVehiclesAllQuoted}
                    onNotesUpdate={() => {
                      notesRef.current?.refresh();
                    }}
                    toggleDispatchModal={toggleDispatchStepsModalVisible}
                  />
                </Col>
              </Row>
            </Col>
            <Col xl={8} lg={24} className={styles.rightColumn}>
              <TotalPriceCard />
              <ShipperTransporterIntegrationsCard
                isOpsAssistedChecked={isOpsAssistedChecked}
                handleToggleOpsAssisted={handleToggleOpsAssisted}
              />

              <Notes
                ref={notesRef}
                type={NoteType.Shipment}
                title="Autosled Notes"
                paginationType={NotesListPaginationType.Simple}
                entityId={adminShipmentData.id}
                configPerm={configPerm}
              />
            </Col>
          </Row>
        </Content>
      </Layout>

      <UpdateLocationModal visible={visibleUpdateLocationModal} onClose={() => setVisibleUpdateLocationModal(false)} />

      <ToggleHoldModal
        visible={onHoldVisible}
        adminShipmentData={adminShipmentData}
        onClose={() => {
          setOnHoldVisible(false);
          notesRef.current.refresh();
        }}
      />

      <ChangeFlagModal
        visible={askingForFlagNotes !== null}
        flagType={askingForFlagNotes}
        shipmentExpedited={shipmentExpedited}
        problemRide={problemRide}
        isDamaged={isDamaged}
        onCancel={cancelFlagToggle}
        onClose={() => setAskingForFlagNotes(null)}
        refreshNotes={() => {
          notesRef.current?.refresh();
        }}
      />

      <HardRouteModal
        visible={hardRoutePopupVisible}
        hardRouteLocation={hardRouteLocation}
        onClose={newHardRouteLocation => {
          notesRef.current?.refresh();

          const newState = {
            hardRoutePopupVisible: false,
          };

          if (typeof newHardRouteLocation === "number") {
            newState.hardRouteLocation = newHardRouteLocation;
          }

          setHardRouteLocation(newState.hardRouteLocation);
          setHardRoutePopupVisible(newState.hardRoutePopupVisible);
        }}
      />

      <DealerExchangeModal
        isAdmin
        shipmentId={adminShipmentData.id}
        hideModal={() => {
          dispatch({
            type: "adminShipmentView/toggleDealerExchangeModalVisible",
            payload: false,
          });
        }}
        onSuccess={onDealerExchangeSuccess}
      />

      <CancelShipmentModal
        visible={cancelModalVisible}
        onClose={() => setCancelModalVisible(false)}
        afterSubmit={() => {
          setCancelModalVisible(false);
          initializePageData();
        }}
        shipmentId={adminShipmentData?.id}
        shipmentStatus={adminShipmentData?.status}
      />

      {dispatchStepsModalVisible && (
        <DispatchStepsModal
          visible={dispatchStepsModalVisible}
          setVisible={toggleDispatchStepsModalVisible}
          selectedShipments={[adminShipmentData]}
          refreshParentTable={() => {
            initializePageData();
          }}
        />
      )}
    </PageHeaderWrapper>
  );
};

export default connect(({ adminShipmentView, loading, configs }) => ({
  adminShipmentView,
  loading: loading.effects["adminShipmentView/fetchSingleAdminShipment"],
  loadingLocation: loading.effects["adminShipmentView/updateLoc"],
  loadingOpsAssisted: loading.effects["adminShipmentView/toggleOpsAssisted"],
  configPerm: configs.configPerm,
  securePassEnabled: configs.securePass.enabled,
}))(AdminShipmentView);
